// TravelAreaAnalysisReport.jsx - Heavy Travel Areas Report
// Identifies geographic areas with the most mileage activity
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const TravelAreaAnalysisReport = () => {
  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;

  // Calculate current month date range for defaults
  const getCurrentMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split("T")[0],
      end: lastDay.toISOString().split("T")[0],
    };
  };

  const defaultDates = getCurrentMonthRange();

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expandedCities, setExpandedCities] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Map());
  const [filters, setFilters] = useState({
    customer_id: "",
    user_id: "",
    start_date: defaultDates.start,
    end_date: defaultDates.end,
    group_by: "city",
  });
  const [customers, setCustomers] = useState([]);
  const [jobCoaches, setJobCoaches] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [customerRes, userRes] = await Promise.all([
          secureAxios.get("/api/customers", { params: { tenant_id } }),
          secureAxios.get("/api/users", {
            params: { tenant_id, role: "Job Coach" },
          }),
        ]);
        setCustomers(customerRes.data || []);
        setJobCoaches(userRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/travel-area-analysis",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            user_id: filters.user_id || undefined,
            start_date: filters.start_date || undefined,
            end_date: filters.end_date || undefined,
            group_by: filters.group_by,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);
        setExpandedCities(new Set());
        setExpandedSections(new Map());

        await auditApi.logEntry({
          action: "GENERATE_TRAVEL_AREA_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading travel area report:", err);
      toast.error("Failed to load travel area analysis report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Formatting helpers
  const formatCurrency = (num) => {
    return "$" + Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatNumber = (num, decimals = 1) => {
    return Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatMiles = (num) => {
    return Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Toggle city expansion
  const toggleCity = (displayName) => {
    setExpandedCities((prev) => {
      const next = new Set(prev);
      if (next.has(displayName)) {
        next.delete(displayName);
      } else {
        next.add(displayName);
      }
      return next;
    });
  };

  // Toggle section within a city (consumers / job_coaches)
  const toggleSection = (cityKey, section) => {
    const key = `${cityKey}__${section}`;
    setExpandedSections((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, true);
      }
      return next;
    });
  };

  const isSectionExpanded = (cityKey, section) => {
    return expandedSections.has(`${cityKey}__${section}`);
  };

  // Expand / collapse all
  const expandAll = () => {
    const allCities = new Set(reportData.map((d) => d.display_name));
    setExpandedCities(allCities);
    const allSections = new Map();
    reportData.forEach((d) => {
      allSections.set(`${d.display_name}__consumers`, true);
      allSections.set(`${d.display_name}__job_coaches`, true);
    });
    setExpandedSections(allSections);
  };

  const collapseAll = () => {
    setExpandedCities(new Set());
    setExpandedSections(new Map());
  };

  // Get intensity color based on total miles relative to max
  const getIntensityBadge = (miles, maxMiles) => {
    if (!maxMiles || maxMiles === 0) return "secondary";
    const ratio = miles / maxMiles;
    if (ratio >= 0.75) return "danger";
    if (ratio >= 0.5) return "warning";
    if (ratio >= 0.25) return "info";
    return "secondary";
  };

  // Get margin color
  const getMarginColor = (margin) => {
    if (margin >= 30) return "text-success";
    if (margin >= 10) return "text-primary";
    if (margin >= 0) return "text-warning";
    return "text-danger";
  };

  const maxMiles = reportData.length > 0
    ? Math.max(...reportData.map((d) => d.total_miles))
    : 0;

  return (
    <div>
      <h4 className="mb-3">
        <i className="bi bi-geo-alt me-2"></i>
        Travel Area Analysis
      </h4>
      <p className="text-muted mb-4">
        Identifies geographic areas with the highest mileage activity.
        Aggregates travel by consumer city to help optimize service delivery
        routes and identify regional cost patterns.
      </p>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label small fw-bold">Customer</label>
              <select
                className="form-select form-select-sm"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    customer_id: e.target.value,
                  }))
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small fw-bold">Job Coach</label>
              <select
                className="form-select form-select-sm"
                value={filters.user_id}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, user_id: e.target.value }))
                }
              >
                <option value="">All Job Coaches</option>
                {jobCoaches.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small fw-bold">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
              />
            </div>

            <div className="col-md-2">
              <label className="form-label small fw-bold">End Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
              />
            </div>

            <div className="col-md-2">
              <label className="form-label small fw-bold">Group By</label>
              <select
                className="form-select form-select-sm"
                value={filters.group_by}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    group_by: e.target.value,
                  }))
                }
              >
                <option value="city">City</option>
                <option value="postal_code">Postal Code</option>
              </select>
            </div>

            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                onClick={() =>
                  setFilters({
                    customer_id: "",
                    user_id: "",
                    start_date: "",
                    end_date: "",
                    group_by: "city",
                  })
                }
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Analyzing travel patterns...</p>
        </div>
      )}

      {/* Summary Cards */}
      {!loading && summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3 col-xl">
            <div className="card bg-primary bg-opacity-10 h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Areas Served</div>
                <div className="h4 mb-0 text-primary">
                  {summary.total_areas}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3 col-xl">
            <div className="card bg-info bg-opacity-10 h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Total Trips</div>
                <div className="h4 mb-0 text-info">
                  {formatNumber(summary.total_trips, 0)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3 col-xl">
            <div className="card bg-warning bg-opacity-10 h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Total Miles</div>
                <div className="h4 mb-0 text-warning">
                  {formatMiles(summary.total_miles)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3 col-xl">
            <div className="card bg-danger bg-opacity-10 h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Travel Cost</div>
                <div className="h4 mb-0 text-danger">
                  {formatCurrency(summary.total_cost)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3 col-xl">
            <div className="card bg-success bg-opacity-10 h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Mileage Revenue</div>
                <div className="h4 mb-0 text-success">
                  {formatCurrency(summary.total_revenue)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Secondary summary row */}
      {!loading && summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Consumers Served</div>
                <div className="h5 mb-0">{summary.unique_consumers}</div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Job Coaches</div>
                <div className="h5 mb-0">{summary.unique_job_coaches}</div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">Avg Miles/Trip</div>
                <div className="h5 mb-0">
                  {formatNumber(summary.avg_miles_per_trip)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100">
              <div className="card-body py-2 px-3 text-center">
                <div className="small text-muted">
                  Overall Margin
                </div>
                <div className={`h5 mb-0 ${getMarginColor(summary.overall_margin)}`}>
                  {formatNumber(summary.overall_margin)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Non-billable notice */}
      {!loading && summary && summary.non_billable_miles > 0 && (
        <div className="alert alert-secondary py-2 mb-4">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Non-billable travel:</strong>{" "}
          {formatMiles(summary.non_billable_miles)} miles across{" "}
          {summary.non_billable_trips} trips (est. cost:{" "}
          {formatCurrency(summary.non_billable_cost)}). These are not included
          in the area breakdown below.
        </div>
      )}

      {/* Expand/Collapse controls */}
      {!loading && reportData.length > 0 && (
        <div className="d-flex justify-content-end mb-2 gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={expandAll}
          >
            <i className="bi bi-arrows-expand me-1"></i>Expand All
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={collapseAll}
          >
            <i className="bi bi-arrows-collapse me-1"></i>Collapse All
          </button>
        </div>
      )}

      {/* Main Data Table */}
      {!loading && reportData.length > 0 && (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover table-sm mb-0 align-middle">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "30px" }}></th>
                  <th>Area</th>
                  <th className="text-center">Trips</th>
                  <th className="text-end">Total Miles</th>
                  <th className="text-end">Avg/Trip</th>
                  <th className="text-center">Consumers</th>
                  <th className="text-center">Coaches</th>
                  <th className="text-end">Cost</th>
                  <th className="text-end">Revenue</th>
                  <th className="text-end">Profit</th>
                  <th className="text-end">Margin</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((area, idx) => (
                  <React.Fragment key={area.display_name}>
                    {/* City/Area Row */}
                    <tr
                      className="cursor-pointer"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleCity(area.display_name)}
                    >
                      <td className="text-center">
                        <i
                          className={`bi ${
                            expandedCities.has(area.display_name)
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`badge bg-${getIntensityBadge(
                              area.total_miles,
                              maxMiles
                            )}`}
                            title="Travel intensity"
                          >
                            #{idx + 1}
                          </span>
                          <div>
                            <strong>
                              <i className="bi bi-geo-alt me-1"></i>
                              {area.display_name}
                            </strong>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark">
                          {area.trip_count}
                        </span>
                      </td>
                      <td className="text-end fw-bold">
                        {formatMiles(area.total_miles)}
                      </td>
                      <td className="text-end text-muted">
                        {formatNumber(area.avg_miles_per_trip)}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-success bg-opacity-75">
                          {area.consumer_count}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-primary bg-opacity-75">
                          {area.job_coach_count}
                        </span>
                      </td>
                      <td className="text-end">
                        <span title={`Paid: ${formatCurrency(area.cost_paid)} | Unpaid: ${formatCurrency(area.cost_unpaid)}`}>
                          {formatCurrency(area.total_cost)}
                        </span>
                      </td>
                      <td className="text-end">
                        <span title={`Invoiced: ${formatCurrency(area.revenue_invoiced)} | Not Invoiced: ${formatCurrency(area.revenue_not_invoiced)}`}>
                          {formatCurrency(area.total_revenue)}
                        </span>
                      </td>
                      <td className="text-end">
                        <span
                          className={
                            area.profit >= 0 ? "text-success" : "text-danger"
                          }
                        >
                          {formatCurrency(area.profit)}
                        </span>
                      </td>
                      <td className="text-end">
                        <span className={getMarginColor(area.margin)}>
                          {formatNumber(area.margin)}%
                        </span>
                      </td>
                    </tr>

                    {/* Expanded detail: Consumers & Job Coaches */}
                    {expandedCities.has(area.display_name) && (
                      <tr>
                        <td colSpan="11" className="p-0">
                          <div className="bg-light p-3">
                            {/* Cost breakdown bar */}
                            <div className="row mb-3">
                              <div className="col-md-6">
                                <div className="small text-muted mb-1">
                                  Cost Breakdown
                                </div>
                                <div className="d-flex gap-3">
                                  <span className="small">
                                    <i className="bi bi-check-circle-fill text-success me-1"></i>
                                    Paid: {formatCurrency(area.cost_paid)}
                                  </span>
                                  <span className="small">
                                    <i className="bi bi-clock-fill text-warning me-1"></i>
                                    Unpaid: {formatCurrency(area.cost_unpaid)}
                                  </span>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="small text-muted mb-1">
                                  Revenue Breakdown
                                </div>
                                <div className="d-flex gap-3">
                                  <span className="small">
                                    <i className="bi bi-receipt text-success me-1"></i>
                                    Invoiced:{" "}
                                    {formatCurrency(area.revenue_invoiced)}
                                  </span>
                                  <span className="small">
                                    <i className="bi bi-hourglass-split text-warning me-1"></i>
                                    Not Invoiced:{" "}
                                    {formatCurrency(area.revenue_not_invoiced)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Consumers Section */}
                            <div className="mb-3">
                              <div
                                className="d-flex align-items-center gap-2 mb-2"
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSection(
                                    area.display_name,
                                    "consumers"
                                  );
                                }}
                              >
                                <i
                                  className={`bi ${
                                    isSectionExpanded(
                                      area.display_name,
                                      "consumers"
                                    )
                                      ? "bi-chevron-down"
                                      : "bi-chevron-right"
                                  }`}
                                ></i>
                                <strong className="small">
                                  <i className="bi bi-people me-1"></i>
                                  Consumers ({area.consumer_count})
                                </strong>
                              </div>
                              {isSectionExpanded(
                                area.display_name,
                                "consumers"
                              ) && (
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered mb-0 bg-white">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Consumer</th>
                                        <th>Customer</th>
                                        <th className="text-center">Trips</th>
                                        <th className="text-end">Miles</th>
                                        <th className="text-end">Cost</th>
                                        <th className="text-end">Revenue</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {area.consumers.map((consumer) => (
                                        <tr key={consumer.consumer_id}>
                                          <td>{consumer.consumer_name}</td>
                                          <td className="text-muted small">
                                            {consumer.customer_name}
                                          </td>
                                          <td className="text-center">
                                            {consumer.trip_count}
                                          </td>
                                          <td className="text-end">
                                            {formatMiles(consumer.total_miles)}
                                          </td>
                                          <td className="text-end">
                                            {formatCurrency(
                                              consumer.total_cost
                                            )}
                                          </td>
                                          <td className="text-end">
                                            {formatCurrency(
                                              consumer.total_revenue
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Job Coaches Section */}
                            <div>
                              <div
                                className="d-flex align-items-center gap-2 mb-2"
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSection(
                                    area.display_name,
                                    "job_coaches"
                                  );
                                }}
                              >
                                <i
                                  className={`bi ${
                                    isSectionExpanded(
                                      area.display_name,
                                      "job_coaches"
                                    )
                                      ? "bi-chevron-down"
                                      : "bi-chevron-right"
                                  }`}
                                ></i>
                                <strong className="small">
                                  <i className="bi bi-person-badge me-1"></i>
                                  Job Coaches ({area.job_coach_count})
                                </strong>
                              </div>
                              {isSectionExpanded(
                                area.display_name,
                                "job_coaches"
                              ) && (
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered mb-0 bg-white">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Job Coach</th>
                                        <th className="text-center">Trips</th>
                                        <th className="text-end">Miles</th>
                                        <th className="text-center">
                                          Consumers Served
                                        </th>
                                        <th className="text-end">Cost</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {area.job_coaches.map((coach) => (
                                        <tr key={coach.user_id}>
                                          <td>{coach.job_coach_name}</td>
                                          <td className="text-center">
                                            {coach.trip_count}
                                          </td>
                                          <td className="text-end">
                                            {formatMiles(coach.total_miles)}
                                          </td>
                                          <td className="text-center">
                                            <span className="badge bg-primary bg-opacity-50">
                                              {coach.consumer_count}
                                            </span>
                                          </td>
                                          <td className="text-end">
                                            {formatCurrency(coach.total_cost)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                {/* Totals Row */}
                {summary && (
                  <tr className="table-dark fw-bold">
                    <td></td>
                    <td>
                      Totals ({summary.total_areas} area
                      {summary.total_areas !== 1 ? "s" : ""})
                    </td>
                    <td className="text-center">{summary.total_trips}</td>
                    <td className="text-end">
                      {formatMiles(summary.total_miles)}
                    </td>
                    <td className="text-end">
                      {formatNumber(summary.avg_miles_per_trip)}
                    </td>
                    <td className="text-center">{summary.unique_consumers}</td>
                    <td className="text-center">
                      {summary.unique_job_coaches}
                    </td>
                    <td className="text-end">
                      {formatCurrency(summary.total_cost)}
                    </td>
                    <td className="text-end">
                      {formatCurrency(summary.total_revenue)}
                    </td>
                    <td className="text-end">
                      <span
                        className={
                          summary.total_profit >= 0
                            ? "text-success"
                            : "text-danger"
                        }
                      >
                        {formatCurrency(summary.total_profit)}
                      </span>
                    </td>
                    <td className="text-end">
                      <span className={getMarginColor(summary.overall_margin)}>
                        {formatNumber(summary.overall_margin)}%
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && reportData.length === 0 && (
        <div className="card">
          <div className="card-body text-center py-5">
            <i
              className="bi bi-geo-alt text-muted"
              style={{ fontSize: "3rem" }}
            ></i>
            <h5 className="mt-3 text-muted">No Travel Data Found</h5>
            <p className="text-muted">
              No billable mileage logs with consumer addresses were found for
              the selected filters. Ensure consumers have addresses on file and
              mileage logs have been recorded.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelAreaAnalysisReport;